import type { StarterEntry } from "../../ops";

/** Maps campaign starter audienceHint → segment slug used in the wizard / API. */
const AUDIENCE_HINT_TO_SEGMENT: Record<string, string> = {
  all_verified_users: "all",
  all_active_users: "active",
  hot_leads: "hot_leads",
  premium_members: "premium",
  newsletter_subscribers_verified: "newsletter_subscribers",
  engaged_multi_chapter: "upgrade_ready",
};

const CAMPAIGN_EMAIL_SHELL = (innerIntro: string, ctaLabel?: string) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
</head>
<body style="margin:0;padding:0;background:#F5F0E8;font-family:Arial,Helvetica,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#F5F0E8;padding:24px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;background:#FFFFFF;border-radius:8px;overflow:hidden;">
          <tr>
            <td style="background:#1A5F7A;padding:20px 28px;">
              <span style="font-size:22px;font-weight:700;color:#FFFFFF;letter-spacing:0.5px;">LifeScribe</span>
            </td>
          </tr>
          <tr>
            <td style="padding:28px;color:#1B1F23;font-size:16px;line-height:1.6;">
              ${innerIntro}
              {{message_body}}
              ${
                ctaLabel
                  ? `<p style="margin-top:28px;"><a href="https://www.trylifescribe.com" style="display:inline-block;background:#D4A017;color:#1B1F23;text-decoration:none;font-weight:700;padding:12px 24px;border-radius:6px;">${ctaLabel}</a></p>`
                  : ""
              }
            </td>
          </tr>
          <tr>
            <td style="padding:16px 28px 24px;font-size:12px;color:#666666;border-top:1px solid #E8E4DC;">
              You received this because you are a LifeScribe member.
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

const STARTER_HTML: Record<string, string> = {
  product_announcement: CAMPAIGN_EMAIL_SHELL(
    "<p style=\"margin:0 0 16px;\">Hi {{user_name}},</p><p style=\"margin:0 0 16px;\">We have something new we think you will love:</p>",
    "Explore what's new",
  ),
  promotional_offer: CAMPAIGN_EMAIL_SHELL(
    "<p style=\"margin:0 0 16px;\">Hi {{user_name}},</p><p style=\"margin:0 0 16px;\">For a limited time, we are offering an exclusive discount on LifeScribe:</p>",
    "Claim your offer",
  ),
  newsletter_broadcast: CAMPAIGN_EMAIL_SHELL(
    "<p style=\"margin:0 0 16px;\">Hi {{user_name}},</p><p style=\"margin:0 0 16px;\">Here is what is happening at LifeScribe this week:</p>",
  ),
  feature_launch: CAMPAIGN_EMAIL_SHELL(
    "<p style=\"margin:0 0 16px;\">Hi {{user_name}},</p><p style=\"margin:0 0 16px;\">Have you tried our latest feature yet?</p>",
    "Try it now",
  ),
  holiday_greeting: CAMPAIGN_EMAIL_SHELL(
    "<p style=\"margin:0 0 16px;\">Hi {{user_name}},</p><p style=\"margin:0 0 16px;\">Warm wishes from everyone at LifeScribe.</p>",
  ),
  customer_survey: CAMPAIGN_EMAIL_SHELL(
    "<p style=\"margin:0 0 16px;\">Hi {{user_name}},</p><p style=\"margin:0 0 16px;\">Your feedback helps us build a better LifeScribe for you.</p>",
    "Share feedback",
  ),
};

const STARTER_BODY_SCAFFOLD: Record<string, string> = {
  product_announcement:
    "We are excited to introduce [feature name].\n\n[Describe what it does and why it matters to members.]\n\n[How to get started — one short paragraph.]",
  promotional_offer:
    "For the next 48 hours, save [X]% on your LifeScribe plan.\n\nUse code LAUNCH50 at checkout.\n\n[Add any terms or expiry details.]",
  newsletter_broadcast:
    "• [Highlight 1]\n• [Highlight 2]\n• [Highlight 3]\n\nThank you for being part of the LifeScribe community.",
  feature_launch:
    "[Feature name] is now available in your account.\n\n[One sentence on the benefit.]\n\nOpen the app to try it today.",
  holiday_greeting:
    "We hope this season brings you joy and time with the people who matter most.\n\nThank you for trusting LifeScribe with your stories.",
  customer_survey:
    "We would love to hear about your experience.\n\nIt takes about 2 minutes, and your answers go directly to our product team.",
};

export interface CampaignWizardPrefill {
  starterId: string | null;
  audienceId: string | null;
  subjectLine: string;
  bodyText: string;
  /** Branded HTML shell with {{message_body}} placeholder; null = plain wrap only. */
  bodyHtmlTemplate: string | null;
}

export function prefillFromCampaignStarter(
  starter: StarterEntry,
): CampaignWizardPrefill {
  const id = String(starter.id || "");
  if (!id || id.startsWith("blank")) {
    return {
      starterId: id || null,
      audienceId: null,
      subjectLine: "",
      bodyText: "",
      bodyHtmlTemplate: null,
    };
  }

  const hint = starter.audienceHint as string | undefined;
  const audienceId =
    hint && AUDIENCE_HINT_TO_SEGMENT[hint]
      ? AUDIENCE_HINT_TO_SEGMENT[hint]
      : null;

  const subjectLine = String(
    starter.subjectScaffold || starter.subjectHint || "",
  );
  const bodyText = String(
    starter.bodyTextScaffold || STARTER_BODY_SCAFFOLD[id] || "",
  );
  const bodyHtmlTemplate = STARTER_HTML[id] || null;

  return {
    starterId: id,
    audienceId,
    subjectLine,
    bodyText,
    bodyHtmlTemplate,
  };
}

/** Saved marketing template chosen in campaign / ad-hoc composer. */
export interface SelectedEmailTemplate {
  id: string;
  template_key: string;
  template_name: string;
  subject_line: string;
  html_content: string;
  plain_text_content?: string;
  category?: string;
}

export function prefillFromEmailTemplate(
  template: SelectedEmailTemplate,
): {
  templateId: string;
  templateName: string;
  subjectLine: string;
  bodyText: string;
  bodyHtmlTemplate: string;
} {
  const html = template.html_content || "";
  const plain =
    template.plain_text_content?.trim() ||
    html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return {
    templateId: template.id,
    templateName: template.template_name || template.template_key,
    subjectLine: template.subject_line || "",
    bodyText: plain,
    bodyHtmlTemplate: html,
  };
}

/** Merge wizard plain-text body into branded starter HTML for send. */
export function buildCampaignHtmlContent(
  bodyText: string,
  bodyHtmlTemplate: string | null,
): string {
  const trimmed = bodyText.trim();
  if (!bodyHtmlTemplate) {
    return plainBodyToHtml(trimmed);
  }

  if (!bodyHtmlTemplate.includes("{{message_body}}")) {
    return bodyHtmlTemplate;
  }

  const messageHtml = trimmed
    ? plainBodyToHtml(trimmed, { fragmentOnly: true })
    : "<p style=\"margin:0 0 16px;\">[Your message]</p>";

  return bodyHtmlTemplate.replace("{{message_body}}", messageHtml);
}

function plainBodyToHtml(
  body: string,
  opts?: { fragmentOnly?: boolean },
): string {
  if (!body) return "";
  if (/<[a-z][\s\S]*>/i.test(body)) return body;

  const paragraphs = body
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const withBreaks = p
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      return `<p style="margin:0 0 16px;">${withBreaks}</p>`;
    })
    .join("\n");

  if (opts?.fragmentOnly) return paragraphs;

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #1B1F23; max-width: 600px; margin: 0 auto; padding: 24px;">
${paragraphs}
</body>
</html>`;
}
